<script setup lang="ts">
import HeadBlocks from "@/components/Project/Appointment/HeadBlocks.vue";
import IssuesBlock from "@/components/Project/Appointment/IssuesBlock.vue";
import MeisterNoteBlock from "@/components/Project/Appointment/MeisterNoteBlock.vue";
import PrufBerichtModal from "@/components/Project/Appointment/Modal/PrufBerichtModal.vue";
import AbnameProtocolModal from "@/components/Project/Appointment/Modal/AbnameProtocolModal.vue";
import NoteBlock from "@/components/Project/Appointment/NoteBlock.vue";
import client from "@/utils/axios";
import { getStatusByName } from "@/utils/report-status";
import { defineProps, defineEmits, ref, onMounted } from "vue";
import UpdateAbnahmeProtocolModal from "@/components/Project/Appointment/Modal/UpdateAbnahmeProtocolModal.vue";
import {
  getTerminStatusColor,
  getTerminStatusBorder,
} from "@/utils/status-color";

type Props = {
  termin: any;
  projectName: string;
  isIssuesFeatureFlagActive: boolean;
};

type Emits = {
  (event: "refresh"): void;
};

const props = defineProps<Props>();
defineEmits<Emits>();

const prufberichtModalOpen = ref(false);
const abnahmeProtocolModalOpen = ref(false);
const prufberichtReportData = ref<any>(null);
const abnahmeprotokollReportData = ref<any>();
const isSignedDocument = ref(false);

const isUpdateAbnahmeProtocolModal = ref<boolean>(false);

const getPrufberichtReportData = async () => {
  try {
    let resp = await client.get(
      `/termin/${props.termin.id}/prufbericht-report`
    );

    prufberichtReportData.value = resp.data;
  } catch (error: any) {
    console.error(error);
  }
};

const getAbnahmeprotokollReportData = async () => {
  try {
    let resp = await client.get(
      `/termin/${props.termin.id}/abnahmeprotokoll-report`
    );
    abnahmeprotokollReportData.value = resp.data;
  } catch (error: any) {
    console.error(error);
  }
};

const openPrufberichtModal = async () => {
  await getPrufberichtReportData();
  prufberichtModalOpen.value = true;
};
const openAbnahmeprotokollModal = async () => {
  await getAbnahmeprotokollReportData();
  abnahmeProtocolModalOpen.value = true;
};

onMounted(async () => {
  if ([1, 2].includes(props.termin.phase_progress?.phase?.id)) {
    await getPrufberichtReportData();
    await getAbnahmeprotokollReportData();
  }
});
</script>

<template>
  <div
    class="w-full mt-[30px] h-[calc(100vh-110px)] pl-3 sm:pl-0 overflow-y-auto"
  >
    <div class="max-w-[1156px] mx-auto">
      <!-- Header -->
      <div class="flex items-center gap-2 pb-[9px]">
        <div class="flex items-center gap-[2px]">
          <p
            class="text-xl leading-[26px] tracking-[0.01rem] text-[#616068] font-medium"
          >
            {{ termin.name }}
          </p>
        </div>
        <span
          class="text-xs text-primary-text-color font-medium py-[2px] px-[6px] rounded-primary-radius border-[1px]"
          :style="{
            borderColor: `${getTerminStatusBorder(termin.termin_status?.id)}`,
            backgroundColor: `${getTerminStatusColor(
              termin.termin_status?.id
            )}`,
          }"
          >{{
            termin.termin_status ? termin.termin_status?.name : "undefined"
          }}</span
        >
      </div>
      <!-- Head blocks -->
      <div>
        <HeadBlocks :termin="termin" />
      </div>
      <div
        v-if="
          termin.phase_progress?.phase.id === 2 ||
          termin.phase_progress?.phase.id === 1
        "
        class="flex items-center gap-[12.5px]"
      >
        <!-- Abnahme protocol -->
        <div class="flex-1">
          <div
            class="flex items-center justify-between p-3 rounded-primary-radius bg-[#E5E4E2] hover:bg-[#dfdedc] duration-500"
          >
            <div class="flex items-center gap-2">
              <h3
                class="text-primary-text-color text-xl leading-[32px] font-semibold tracking-[0.01rem]"
              >
                Abnameprotokoll
              </h3>
              <p
                v-if="
                  abnahmeprotokollReportData &&
                  abnahmeprotokollReportData?.status
                "
                class="text-[14px] tracking-[0.01rem] text-primary-text-color font-medium py-[2px] px-[6px] rounded-primary-radius bg-[#A7A5AE]"
              >
                {{
                  abnahmeprotokollReportData
                    ? getStatusByName(abnahmeprotokollReportData?.status)
                    : ""
                }}
              </p>
              <!-- PDF-stale warning: signatures/inputs saved but PDF regen
                   failed. Open the protocol card and use Force Regenerate. -->
              <p
                v-if="abnahmeprotokollReportData?.pdf_stale"
                class="text-[12px] tracking-[0.01rem] text-white font-semibold py-[2px] px-[6px] rounded-primary-radius bg-[#D97706]"
                title="Signatures are saved but the PDF was not regenerated. Open the protocol and use Force Regenerate."
              >
                PDF outdated
              </p>
            </div>
            <div class="flex items-center gap-2">
              <button
                v-if="abnahmeprotokollReportData?.status !== 'signed'"
                @click="
                  () => {
                    isSignedDocument = false;
                    isUpdateAbnahmeProtocolModal = true;
                  }
                "
              >
                <img src="@/assets/paperclip-attachment.svg" alt="Update" />
              </button>
              <button
                v-if="abnahmeprotokollReportData?.status !== 'signed'"
                @click="openAbnahmeprotokollModal"
              >
                <img src="@/assets/edit-plus-icon.svg" alt="Edit icon" />
              </button>
              <!-- Open the modal for signed/stale reports too, so admin can
                   trigger Force Regenerate from inside it. -->
              <button
                v-if="
                  abnahmeprotokollReportData?.status === 'signed' ||
                  abnahmeprotokollReportData?.pdf_stale
                "
                @click="openAbnahmeprotokollModal"
                title="View / Force Regenerate"
              >
                <img src="@/assets/edit-plus-icon.svg" alt="View icon" />
              </button>
            </div>
          </div>
          <!-- Abnahme protocol modal -->
          <div v-if="abnahmeProtocolModalOpen">
            <AbnameProtocolModal
              :termin="termin"
              :data="abnahmeprotokollReportData"
              @close="abnahmeProtocolModalOpen = false"
              @refresh="
                async () => {
                  await getAbnahmeprotokollReportData();
                }
              "
              @upload-signed-version="
                () => {
                  isSignedDocument = true;
                  abnahmeProtocolModalOpen = false;
                  isUpdateAbnahmeProtocolModal = true;
                }
              "
            />
          </div>
          <!-- Update abnhame protocol modal -->
          <div v-if="isUpdateAbnahmeProtocolModal">
            <UpdateAbnahmeProtocolModal
              :is-signed-document="isSignedDocument"
              :termin="props.termin.id"
              @close="isUpdateAbnahmeProtocolModal = false"
              @refresh="
                async () => {
                  await getAbnahmeprotokollReportData();
                }
              "
            />
          </div>
        </div>
        <!-- Prufbericht -->
        <div class="flex-1">
          <div
            @click="openPrufberichtModal"
            class="flex items-center justify-between p-3 rounded-primary-radius bg-[#E5E4E2] cursor-pointer hover:bg-[#dfdedc] duration-500"
          >
            <h3
              class="text-primary-text-color text-xl leading-[32px] font-semibold tracking-[0.01rem]"
            >
              Prüfbericht
            </h3>
            <p
              v-if="prufberichtReportData?.status"
              class="text-[14px] tracking-[0.01rem] text-primary-text-color font-medium py-[2px] px-[6px] rounded-primary-radius bg-[#A7A5AE]"
            >
              {{
                prufberichtReportData
                  ? getStatusByName(prufberichtReportData?.status)
                  : ""
              }}
            </p>
          </div>
          <!-- Prufbericht modal -->
          <div v-if="prufberichtModalOpen && prufberichtReportData">
            <PrufBerichtModal
              :termin="termin"
              :data="prufberichtReportData"
              @close="prufberichtModalOpen = false"
            />
          </div>
        </div>
      </div>
      <!-- Issues block -->
      <div v-if="termin.issues" class="mt-3">
        <IssuesBlock
          :is-issues-feature-flag-active="isIssuesFeatureFlagActive"
          :issue-types="termin.issueTypes?.map((item:any)=> {
            return {
              id: item.id,
              name: item.name
            }
          })"
          :issue-block-types="termin.issueBlockTypes?.map((item:any)=> {
            return {
              id: item.id,
              name: item.name
            }
          })"
          :issues="termin.issues"
          :termin="termin.id"
          @refresh="$emit('refresh')"
        />
      </div>
      <!-- Notes block -->
      <div v-if="termin.notes" class="mt-3">
        <NoteBlock
          :termin="termin.id"
          @refresh="$emit('refresh')"
          :notes="termin.notes"
        />
      </div>

      <!-- Note block -->
      <div class="mt-3">
        <MeisterNoteBlock
          :termin="termin.id"
          :meister-note="termin.meisterNote"
          @refresh="$emit('refresh')"
        />
      </div>
    </div>
  </div>
</template>
